import React from 'react';
import { usePagination } from '@/hooks/usePagination';
import Pagination from './Pagination';
import { PageSizeSelector } from './PageSizeSelector';
import { PaginationInfo } from './PaginationInfo';
import { classNames } from '@/utils/classNames';

interface PaginatedCardsProps<T> {
  data: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  keyExtractor: string | ((item: T, index: number) => string | number);
  defaultPageSize?: number;
  storageKey?: string;
  className?: string;
  gridClassName?: string;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  emptyMessage?: string;
  loading?: boolean;
  loadingCardCount?: number;
}

export function PaginatedCards<T = any>({
  data,
  renderCard,
  keyExtractor,
  defaultPageSize = 10,
  storageKey,
  className,
  gridClassName = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
  showPageSizeSelector = true,
  showPaginationInfo = true,
  emptyMessage = 'データがありません',
  loading = false,
  loadingCardCount,
}: PaginatedCardsProps<T>) {
  const pagination = usePagination({
    data,
    defaultPageSize,
    storageKey,
  });

  const {
    currentPage,
    totalPages,
    pageSize,
    paginatedData,
    totalItems,
    startIndex,
    endIndex,
    setCurrentPage,
    setPageSize,
  } = pagination;

  // keyExtractorが文字列の場合、関数に変換
  const getKey =
    typeof keyExtractor === 'string' ? (item: T) => (item as any)[keyExtractor] : keyExtractor;

  const cardCount = loadingCardCount || pageSize;

  if (loading) {
    return (
      <div className={classNames('space-y-4', className)}>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4'></div>
          <div className={gridClassName}>
            {Array.from({ length: cardCount }).map((_, i) => (
              <div key={i} className='h-48 bg-gray-100 dark:bg-gray-800 rounded-lg'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('space-y-4', className)}>
      {/* ページサイズセレクターと情報表示 */}
      {(showPageSizeSelector || showPaginationInfo) && totalItems > 0 && (
        <div className='flex items-center justify-between flex-wrap gap-4'>
          {showPageSizeSelector && (
            <PageSizeSelector
              pageSize={pageSize}
              totalItems={totalItems}
              onPageSizeChange={setPageSize}
            />
          )}
          {showPaginationInfo && (
            <PaginationInfo
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={totalItems}
            />
          )}
        </div>
      )}

      {/* カードグリッド */}
      {totalItems === 0 ? (
        <div className='text-center py-12 text-gray-500 dark:text-gray-400'>{emptyMessage}</div>
      ) : (
        <div className={gridClassName}>
          {paginatedData.map((item, index) =>
            React.cloneElement(renderCard(item, index) as React.ReactElement, {
              key: getKey(item, index),
            })
          )}
        </div>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className='flex justify-center'>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            showPrevNext={true}
            showFirstLast={true}
            maxVisible={5}
          />
        </div>
      )}
    </div>
  );
}
